import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { FirstSteps } from "@/components/dashboard/FirstSteps";
import { BigPaymentsCard } from "@/components/dashboard/BigPayments";
import { IncomeExpenseChart, Kpis } from "@/components/dashboard/Charts";
import { WealthCards } from "@/components/dashboard/WealthCards";
import { Composition } from "@/components/dashboard/Insights";
import { AccountsCard, BudgetCard, RecentCard, UpcomingCard } from "@/components/dashboard/Lists";
import { MentorStrip } from "@/components/dashboard/MentorStrip";
import { CustomizeDialog, useDashboardBlocks } from "@/components/dashboard/CustomizeDialog";
import { api, formatApiError } from "@/lib/api";
import type { DashboardResponse, Period } from "@/lib/dashboard";
import { money, monthLabel } from "@/lib/format";

export default function DashboardPage() {
  const [period, setPeriod] = useState<Period>("monthly");
  const [d, setD] = useState<DashboardResponse | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const { blocks, toggle } = useDashboardBlocks();
  useEffect(() => {
    api.get<DashboardResponse>("/dashboard", { params: { period } }).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [period]);

  return (
    <section data-testid="dashboard-page">
      <PageHeader title={d ? monthLabel(d.month).replace(/^./, (c) => c.toUpperCase()) : "Resumen"} testId="dashboard-title" />
      <FirstSteps />
      {!d ? <div className="h-64 animate-pulse rounded-[14px] bg-white" /> : (
        <div className="fade-up space-y-6">
          <MentorStrip d={d} />
          <Kpis d={d} />
          <BudgetCard d={d} />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.7fr_1fr]">
            <div className="min-w-0">
              <IncomeExpenseChart d={d} period={period} onPeriod={setPeriod} />
            </div>
            <Composition d={d} period={period} />
          </div>
          <WealthCards d={d} />
          {d.has_liabilities && (
            <Link to="/cuentas" className="block rounded-[14px] border border-[#DDE1E8] bg-white px-6 py-4 text-sm text-slate-600 transition-colors hover:bg-[#FAF7F1]" data-testid="dashboard-interest-paid">
              Pagaste <b className="tabular text-[#B23A1E]" data-testid="dashboard-interest-paid-value">{money(d.interest_paid_year ?? 0)}</b> en intereses este año, entre tarjetas y créditos. <span className="underline underline-offset-2">Ver por cuenta</span>
            </Link>
          )}
          <BigPaymentsCard d={d} />
          {(blocks.recent || blocks.upcoming || blocks.accounts) && (
            <div className="grid gap-6 lg:grid-cols-2" data-testid="optional-blocks">
              {blocks.recent && <RecentCard d={d} />}
              {blocks.upcoming && <UpcomingCard d={d} />}
              {blocks.accounts && <AccountsCard d={d} />}
            </div>
          )}
          <div className="pt-2 text-center">
            <button onClick={() => setCustomOpen(true)} className="text-sm text-slate-500 underline-offset-4 transition-colors hover:text-[#16294D] hover:underline" data-testid="customize-dashboard-link">Personalizar resumen</button>
          </div>
          <CustomizeDialog open={customOpen} onOpenChange={setCustomOpen} blocks={blocks} toggle={toggle} />
        </div>
      )}
    </section>
  );
}
